import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import EmbeddedSearch from "@/components/EmbeddedSearch";
import {
  ButtonSizes,
  ButtonTypes,
  IconTypes,
  StyledButton,
} from "@/components/StyledButton";
import { OverviewDrawer } from "@/components/navigator/OverviewDrawer";
import { OverviewMap } from "@/components/navigator/OverviewMap";
import { OverviewToggle } from "@/components/navigator/OverviewToggle";
import { OverviewList } from "@/components/navigator/OverviewList";
import { useNavigator } from "@/contexts/navigator/NavigatorContext";
import colors from "@/lib/static/colors";
import responsive from "@/lib/static/responsive";
import { handleOutboundClick } from "@/lib/analytics/navigation";
import { handleEmbeddedSearchRequest } from "@/lib/analytics/search";
import ArrowRight from "../icons/ArrowRight";
import { SearchResult } from "../Search";
import { OutcomeAreaFilterPills } from "../OutcomeAreaFilterPills";
import { OutcomeArea } from "@/lib/models";
import { useEntities } from "@/contexts/entities/EntitiesContext";

const studentPerformanceFrameworkHref =
  " https://xqcompetencies.xqsuperschool.org/about";

export const LearnerOutcomeOverview = () => {
  const {
    selectedEntity,
    handleDrawerSelection,
    resetDrawerSelection,
    isOverviewMapActive,
    toggleOverviewMap,
  } = useNavigator();
  const [searchResultIds, setSearchResultIds] = useState<string[]>([]);
  const [filterResultIds, setFilterResultIds] = useState<string[]>([]);

  const isMediumOrLarger = useMediaQuery({
    minWidth: responsive.medium,
  });

  const { outcomeAreas } = useEntities();

  const [filteredOutcomeAreas, setFilteredOutcomeAreas] = useState<
    OutcomeArea[]
  >([]);

  const addToFilteredOutcomeAreaList = (outcomeArea: OutcomeArea) => {
    setFilteredOutcomeAreas((areas) => [...areas, outcomeArea]);
  };

  const removeFromFilteredOutcomeAreaList = (outcomeArea: OutcomeArea) => {
    setFilteredOutcomeAreas((areas) =>
      areas.filter((oA) => oA !== outcomeArea),
    );
  };

  useEffect(() => {
    if (filteredOutcomeAreas.length == 0) {
      setFilterResultIds([]);
    } else {
      const filteredCompetencies = filteredOutcomeAreas.flatMap(
        (outcomeArea) => {
          return outcomeArea.competencies;
        },
      );
      setFilterResultIds(filteredCompetencies);
    }
  }, [filteredOutcomeAreas]);

  const isOverviewMapVisible = isOverviewMapActive && isMediumOrLarger;

  const trackSearch = (searchTerm: string, searchResults?: SearchResult[]) => {
    handleEmbeddedSearchRequest({
      searchTerm,
      context: isOverviewMapVisible ? "map" : "list",
      searchResults,
    });
  };

  const trackLearnMore = () => {
    handleOutboundClick(studentPerformanceFrameworkHref);
  };

  return (
    <div className="grid max-w-screen-large grid-cols-4 gap-x-4 gap-y-8 small:grid-cols-8 medium:grid-cols-12 large:mx-auto large:gap-x-6">
      <div className="col-span-full mt-8 px-4 text-center medium:mt-[60px] medium:px-6">
        <h1 className="font-roobert text-[32px] leading-tight text-black small:text-[40px] medium:leading-none">
          Discover actionable competencies that work with your standards.
        </h1>
        <h2 className="mt-8 font-roobert text-lg font-medium leading-none text-dark-color small:text-2xl medium:mt-10">
          {
            "Tell us what you're teaching, and we'll suggest relevant XQ competencies."
          }
        </h2>
      </div>

      <EmbeddedSearch
        setSearchResultIds={setSearchResultIds}
        trackSearch={trackSearch}
      />

      <div className="col-span-full -mx-4 overflow-x-scroll medium:mx-0">
        <OutcomeAreaFilterPills
          outcomeAreas={outcomeAreas}
          includeInFilter={addToFilteredOutcomeAreaList}
          excludeFromFilter={removeFromFilteredOutcomeAreaList}
        />
      </div>

      <OverviewToggle
        isMapActive={isOverviewMapActive}
        handleToggle={toggleOverviewMap}
      />
      {isOverviewMapVisible ? (
        <OverviewMap
          selectedEntity={selectedEntity}
          handleEntityClick={handleDrawerSelection}
          searchResultIds={[...searchResultIds, ...filterResultIds]}
        />
      ) : (
        <OverviewList
          handleEntityClick={handleDrawerSelection}
          selectedEntity={selectedEntity}
          searchResultIds={[...searchResultIds, ...filterResultIds]}
        />
      )}
      <div className="col-span-full mb-8 flex justify-center small:mb-12 medium:mb-[60px]">
        <StyledButton
          buttonType={ButtonTypes.Tertiary}
          size={ButtonSizes.Large}
          iconType={IconTypes.Right}
          as="link"
          href={studentPerformanceFrameworkHref}
          target="_blank"
          onClick={trackLearnMore}
          className="bg-white"
        >
          Learn more about XQ Competencies
          <ArrowRight fill={colors["BG-60"]} />
        </StyledButton>
      </div>

      <OverviewDrawer
        entity={selectedEntity}
        clearEntity={resetDrawerSelection}
      />
    </div>
  );
};
